import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SETTINGS } from '../config/common.settings';
import { BehaviorSubject, tap } from 'rxjs';
import { User } from '../models/user';
import { EncryptionService } from './encryption.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { AdminControlService } from './admin-control.service';

export interface AuthResponseData {
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    status: string;
    regCode: string;
    mobileNo: string;
    phoneNo: string;
    profilePicture: object;
    description: string;
    position: string;
    residence: string;
    workplace: string;
    firstLogin: string;
    dashboardSettings: object;
    grade: string;
  };
  token: string;
  exp: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenExpirationTimer: any;
  private userObj = new BehaviorSubject<User | null>(null);
  user = this.userObj.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private message: NzMessageService,
    private encryption: EncryptionService,
    private ngxLoader: NgxUiLoaderService,
    private adminControls: AdminControlService,
  ) {}

  login(access: any) {
    const { email, password } = access;
    return this.http
      .post<AuthResponseData>(`${SETTINGS.CMS_API}/api/member/login`, {
        email,
        password,
      }, {withCredentials: true})
      .pipe(
        tap((resData) => {
          this.setSession(
            resData.user.id,
            resData.user.name,
            resData.user.email,
            resData.token,
            +resData.exp,
            resData.user.status,
            resData.user.regCode,
            resData.user.mobileNo,
            resData.user.phoneNo,
            resData.user.profilePicture,
            resData.user.description,
            resData.user.position,
            resData.user.residence,
            resData.user.workplace,
            resData.user.firstLogin,
            resData.user.dashboardSettings,
            resData.user.grade,
          );
          this.updateLog(resData.user);
        }),
      );
  }

  autoLogin() {
    const userData: {
      _id: string;
      name: string;
      email: string;
      exp: Date;
      status: string;
      regCode: string;
      mobileNo: string;
      phoneNo: string;
      profilePicture: object;
      description: string;
      position: string;
      residence: string;
      workplace: string;
      firstLogin: string;
      dashboardSettings: object;
      grade: string;
    } = JSON.parse(
      this.encryption.decrypt(
        <string>localStorage.getItem(SETTINGS.LOGGED_IN_USER),
      ),
    );
    if (!userData) {
      return;
    }
    const loadedUser = new User(
      userData._id,
      userData.name,
      userData.email,
      new Date(userData.exp),
      userData.status,
      userData.regCode,
      userData.mobileNo,
      userData.phoneNo,
      userData.profilePicture,
      userData.description,
      userData.position,
      userData.residence,
      userData.workplace,
      userData.firstLogin,
      userData.dashboardSettings,
      userData.grade,
    );

    if (this.getToken()) {
      this.userObj.next(loadedUser);
      const expirationDuration =
        new Date(userData.exp).getTime() - new Date().getTime();
      this.autoLogout(expirationDuration);
    }
  }

  autoLogout(expDuration: number) {
    this.tokenExpirationTimer = setTimeout(() => {
      this.logout();
    }, expDuration);
  }

  private setSession(
    id: string,
    name: string,
    email: string,
    token: string,
    exp: number,
    status: string,
    regCode: string,
    mobileNo: string,
    phoneNo: string,
    profilePicture: object,
    description: string,
    position: string,
    residence: string,
    workplace: string,
    firstLogin: string,
    dashboardSettings: object,
    grade: string,
  ) {
    const expirationDate = new Date(new Date().getTime() + 3 * 60 * 60 * 1000);
    const user = new User(
      id,
      name,
      email,
      expirationDate,
      status,
      regCode,
      mobileNo,
      phoneNo,
      profilePicture,
      description,
      position,
      residence,
      workplace,
      firstLogin,
      dashboardSettings,
      grade,
    );

    this.userObj.next(user);
    this.setLoggedInUser(JSON.stringify(user));
    this.autoLogout(exp);

    // Encrypt the token and store it in local storage
    localStorage.setItem(SETTINGS.ACCESS_TOKEN, this.encryption.encrypt(token));

    // Set a cookie for the token (browser-based cookie)
    // this.setCookie('payload-token', token, 3); // Expires in 3 hours
  }

  /**
   * Helper function to set a browser cookie
   * @param {string} name - The name of the cookie (e.g., 'payload-token')
   * @param {string} value - The value of the cookie (e.g., token string)
   * @param {number} hours - The cookie's expiration time in hours
   */

  private setCookie(
    name: string,
    value: string,
    hours: number,
    isSecure: boolean = false,
  ) {
    const date = new Date();
    date.setTime(date.getTime() + hours * 60 * 60 * 1000);

    const expires = 'expires=' + date.toUTCString();

    let cookieString = `${name}=${value}; ${expires}; path=/; domain=.purple.lk`;

    if (isSecure) {
      cookieString += ' SameSite=None; Secure';
    } else {
      cookieString += ' SameSite=Lax';
    }

    console.log('Cookie : ', cookieString);
    document.cookie = cookieString;
  }

  setLoggedInUser(user: any): void {
    localStorage.setItem(
      SETTINGS.LOGGED_IN_USER,
      this.encryption.encrypt(user),
    );
  }

  getLoggedInUser(): any {
    const userStrEnc: any = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    return JSON.parse(this.encryption.decrypt(userStrEnc));
  }

  logout(): void {
    this.userObj.next(null);
    localStorage.clear();

    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.tokenExpirationTimer = null;
    this.http.post(`${SETTINGS.CMS_API}/api/member/logout`, {}).subscribe(
      () => {
        this.router.navigate(['/auth/login']);
      },
      () => {
        this.router.navigate(['/auth/login']);
      },
    );
  }

  forgotPassword(email: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.CMS_API}/api/member/forgot-password`, { email }, {withCredentials: true})
        .subscribe(
          (response: any) => {
            resolve(response);
          },
          () => {
            this.message.error('Error');
            reject();
          },
        );
    });
  }

  resetPassword(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.CMS_API}/api/member/reset-password`, data, {withCredentials: true})
        .subscribe(
          (response: any) => {
            resolve(response);
          },
          (err) => {
            this.message.error('Error');
            reject();
          },
        );
    });
  }

  getToken(): any {
    if (localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null) {
      const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        return '';
      }
    }
    return false;
  }

  getMemberByEmail(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      return this.http
        .get(`${SETTINGS.CMS_API}/api/member?where[email][equals]=${email}`)
        .subscribe(
          (response) => {
            resolve(response);
          },
          (error) => {
            this.ngxLoader.stop();
            this.message.create('error', 'Failed to retrieve member');
            reject(error);
          },
        );
    });
  }

  updateMember(member: any): Promise<any> {
    this.userObj.next(member);
    member.profilePicture = member.profilePicture.id;
    this.ngxLoader.start();
    return new Promise((resolve, reject) => {
      return this.http
        .patch(`${SETTINGS.CMS_API}/api/member/${member._id}`, member)
        .subscribe(
          (response) => {
            this.message.create('success', 'Profile has been updated!');
            this.ngxLoader.stop();
            resolve(response);
          },
          (error) => {
            this.ngxLoader.stop();
            this.message.create('error', 'Update Failed');
            reject(error);
          },
        );
    });
  }

  updateUser(member: any): Promise<any> {
    this.ngxLoader.start();
    return new Promise((resolve, reject) => {
      return this.http
        .patch(`${SETTINGS.CMS_API}/api/member/${member._id}`, member)
        .subscribe(
          (response) => {
            this.ngxLoader.stop();
            resolve(response);
          },
          (error) => {
            this.ngxLoader.stop();
            this.message.create('error', 'Update Failed');
            reject(error);
          },
        );
    });
  }

  private updateLog(user: any): void {
    this.adminControls.getControls().then((res) => {
      const data = res.memberLog;
      const date = new Date();
      const formattedTime = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      const formattedDate = date.toLocaleDateString([], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      // Calculate padding based on the maximum length in each column
      const maxTimeLength = 8; // Max length for time (hh:mm AM/PM)
      const maxDateLength = 10; // Max length for date (dd/mm/yyyy)
      const maxNameLength = Math.max(user.name.length, 4); // Minimum 4 characters for the name
      const maxEmailLength = Math.max(user.email.length, 5); // Minimum 5 characters for the email

      const log = `${formattedTime.padEnd(maxTimeLength)} | ${formattedDate.padEnd(maxDateLength)} | ${user.name.padEnd(maxNameLength)} | ${user.email.padEnd(maxEmailLength)}`;
      const appendedLog = log + '\n' + data;

      this.adminControls.updateMemberLog({ memberLog: appendedLog });
    });
  }
}
